import { Box, Theme, Typography } from "@outschool/backpack";
import { REFER_CREDIT_CENTS } from "@outschool/business-rules";
import {
  ParentFragmentFragment,
  PastTeachersForParentProfileQuery,
  PastTeachersForParentProfileQueryVariables,
} from "@outschool/gql-frontend-generated";
import { renderCurrencyString, useTranslation } from "@outschool/localization";
import { addParamsToUrl, leaderUrl, searchPath } from "@outschool/routes";
import { useQuery } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import {
  OutreachModal,
  OutreachModalType,
  TeacherVideoModal,
} from "@outschool/ui-components-website";
import React, { useMemo } from "react";

import * as BrainWithGlassesPNG from "../../../../images/brain-with-glasses.png";
import { pastTeachersParentProfileQuery } from "../../../queries/PastTeachersQuery";
import PageModal from "../../PageModal";
import OutReachOrGive20Get20 from "../../sharing/OutReachOrGive20Get20";
import { EmptyProfileCard } from "../EmptyProfileCard";
import { PrivacyDisclaimerText } from "../PrivacyDisclaimerText";
import { LoadingTeacherCard, TeacherCard } from "./TeacherCard";
import { TeacherCarousel } from "./TeacherCarousel";

type Props = {
  parent: ParentFragmentFragment;
  profileLinkUid?: string;
  privacyState?: string | null;
  privateLink: string;
  isCurrentUsersProfile: boolean;
  publicView: boolean;
};

export function MyTeachersSection({
  parent,
  profileLinkUid,
  privacyState,
  privateLink,
  publicView,
  isCurrentUsersProfile,
}: Props) {
  const { currentUser } = useSession();
  const [showShareModalForTeacherAtIndex, setShowShareModalForTeacherAtIndex] =
    React.useState<null | number>(null);
  const [showVideoModalForTeacherAtIndex, setShowVideoModalForTeacherAtIndex] =
    React.useState<null | number>(null);

  const { data, loading, error } = useQuery<
    PastTeachersForParentProfileQuery,
    PastTeachersForParentProfileQueryVariables
  >(pastTeachersParentProfileQuery, {
    variables: {
      uid: parent.uid,
      profileLinkUid,
      publicView,
    },
  });

  const { t } = useTranslation(
    "client\\components\\teacher\\MyTeachersSection",
  );

  const onlyCreditAmount = renderCurrencyString({
    priceInCents: REFER_CREDIT_CENTS,
    showCurrencyCode: false,
  });

  const teachers = useMemo(
    () =>
      data?.pastTeachersForParentProfile.filter(
        teacher => teacher.hasListings,
      ) || [],
    [data],
  );

  const teacher = useMemo(() => {
    if (showShareModalForTeacherAtIndex === null) {
      return;
    }

    return teachers[showShareModalForTeacherAtIndex];
  }, [teachers, showShareModalForTeacherAtIndex]);

  const shareLink = useMemo(() => {
    if (!teacher) {
      return;
    }

    return addParamsToUrl(leaderUrl(teacher.uid, teacher.leader_link), {
      signup: true,
      usid: currentUser?.slug_id,
    });
  }, [currentUser, teacher]);

  const teacherToShowVideoFor =
    typeof showVideoModalForTeacherAtIndex === "number"
      ? teachers[showVideoModalForTeacherAtIndex]
      : undefined;

  if ((!loading && teachers.length === 0 && !isCurrentUsersProfile) || error) {
    return null;
  }

  const isEducator =
    !!currentUser?.teacher_approved_at && !currentUser?.suspended_at;

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          mb: "40px",
          [theme.breakpoints.up("md")]: { mb: "64px" },
        })}
      >
        <Box
          sx={(theme: Theme) => ({
            mx: "1em",
            mb: "1rem",
            [theme.breakpoints.down("sm")]: { mx: 0 },
          })}
        >
          <Typography emphasized component="h2" variant="h6">
            {t("My Teachers")}
          </Typography>
          {isCurrentUsersProfile && (
            <PrivacyDisclaimerText
              privacyState={privacyState}
              privateLink={privateLink}
            />
          )}
        </Box>

        {!!teachers.length && (
          <TeacherCarousel>
            {loading
              ? [0, 0, 0, 0].map((_, index) => (
                  <LoadingTeacherCard key={index} />
                ))
              : teachers.map((teacher, index) => (
                  <TeacherCard
                    key={teacher.uid}
                    teacher={teacher}
                    onShare={() => setShowShareModalForTeacherAtIndex(index)}
                    onWatchVideo={() =>
                      setShowVideoModalForTeacherAtIndex(index)
                    }
                    isCurrentUsersProfile={isCurrentUsersProfile}
                  />
                ))}
          </TeacherCarousel>
        )}
        {!teachers.length && (
          <EmptyProfileCard
            imageUrl={BrainWithGlassesPNG}
            topText={t("You don’t have any teachers yet!")}
            bottomText={t(
              "Your next star educator is right around the corner...",
            )}
            buttonText={t("Find my next teacher")}
            buttonTo={searchPath()}
          />
        )}
      </Box>
      {!isEducator ? (
        <PageModal
          open={!!shareLink}
          onClose={() => setShowShareModalForTeacherAtIndex(null)}
          fullBleed={false}
          sx={{ maxWidth: 450, padding: "medium" }}
        >
          <OutReachOrGive20Get20
            title={t(
              "Share this teacher with a friend - new users will get {{onlyCreditAmount}} off",
              {
                onlyCreditAmount,
              },
            )}
            supportingCopy={t(
              "You’ll also get {{onlyCreditAmount}} when they sign up with your link and take their first class!",
              {
                onlyCreditAmount,
              },
            )}
            link={shareLink || ""}
          />
        </PageModal>
      ) : (
        teacher && (
          <OutreachModal
            open={!!shareLink}
            onClose={() => setShowShareModalForTeacherAtIndex(null)}
            outreachShareUrl={shareLink!}
            modalType={{
              variant: OutreachModalType.teacher,
              teacher: teacher,
            }}
          />
        )
      )}
      {teacherToShowVideoFor && (
        <TeacherVideoModal
          isOpen
          onClose={() => setShowVideoModalForTeacherAtIndex(null)}
          teacher={teacherToShowVideoFor}
        />
      )}
    </>
  );
}
